import { Stack, Title, LoadingOverlay, Tabs } from '@mantine/core';

import { useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useOrgPermissions from '../../hooks/useOrgPermissions';

import UserProfile from '../../components/Forms/UserProfile/UserProfile';

import classes from './Settings.module.css';
import CompanyDetails from './CompanyDetails';
import CurrentUsers from './CurrentUsers';
import InvitedUsers from './InvitedUsers';
import SharePointSettings from './SharePointSettings/SharePointSettings';
import FactsTable from '../../components/FactsTable';
import ProposalFormatSettings from './ProposalFormatSettings';

const Settings: React.FC = () => {
  const { organization, pageSection } = useParams();
  const { userHasPermission, isLoading, isSuccess } = useOrgPermissions();
  const navigate = useNavigate();

  const tabs = [
    {
      label: 'My Profile',
      section: 'profile',
      element: <UserProfile flex={1} />,
      shouldShowSection: true
    },
    {
      label: 'Company Details',
      section: 'company-details',
      element: <CompanyDetails />,
      shouldShowSection: userHasPermission('view')
    },
    {
      label: 'Current Users',
      section: 'org-users',
      element: <CurrentUsers />,
      shouldShowSection: userHasPermission('view_users')
    },
    {
      label: 'Invites',
      section: 'invited-users',
      element: <InvitedUsers />,
      shouldShowSection: userHasPermission('add_user')
    },
    {
      label: 'Proposal Format',
      section: 'proposal-format',
      element: <ProposalFormatSettings />,
      shouldShowSection: userHasPermission('change')
    },
    {
      label: 'Global Facts',
      section: 'global-facts',
      element: <FactsTable style={{ flex: 1 }} />,
      shouldShowSection: userHasPermission('add_memory_items')
    },
    {
      label: 'SharePoint',
      section: 'sharepoint',
      element: <SharePointSettings />,
      shouldShowSection: userHasPermission('add_storage_provider_configs')
    }
  ];

  const currentTab = tabs.find((tab) => tab.section === pageSection);
  // redirect to profile tab if param doesn't exist or user doesn't have permission
  useEffect(() => {
    if (isSuccess && (currentTab === undefined || !currentTab?.shouldShowSection)) {
      navigate(`/org/${organization}/settings/profile`);
    }
  }, [currentTab, isLoading]);
  const visibleTabs = tabs.filter((tab) => tab.shouldShowSection);
  return (
    <Stack flex={1} gap={16} px={16} mt={12} w='100%' ta={'start'} align='stretch'>
      <Title ta={'start'} order={6}> Settings </Title>
      <LoadingOverlay visible={isLoading} />
      <Tabs
        flex={1}
        defaultValue='profile'
        value={pageSection}
        onChange={(value) => {
          navigate(`/org/${organization}/settings/${value}`);
        }}
      >
        <Tabs.List mih={46} mb={16}>
          {visibleTabs.map((tab, index) => (
            <Tabs.Tab
              key={+index}
              value={tab.section}
              className={classes.tabRoot}
              data-active={tab.section === pageSection || undefined}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {visibleTabs.map((tab, index) => (
          <Tabs.Panel key={+index} value={tab.section}>
            {tab.element}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
};

export default Settings;
